/* eslint-disable react/no-unescaped-entities */
import React, { useState, useCallback } from 'react';
import Board from './components/Board';
import Timer from './components/Timer';
import WordSearch from './components/WordSearch';
import { generateBoard } from './utils/diceGenerator';
import './App.css';

declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
  }
}

const App: React.FC = () => {
  const [gridSize, setGridSize] = useState(7);
  const [timerDuration, setTimerDuration] = useState(180);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [board, setBoard] = useState<string[][]>([]);
  const [highlightedCells, setHighlightedCells] = useState<[number, number][]>([]);
  const [showRules, setShowRules] = useState(false);
  const [timerKey, setTimerKey] = useState(0);

  const playGameOverSound = useCallback(() => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    const audioContext = new AudioContextClass();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(440, audioContext.currentTime);

    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(1, audioContext.currentTime + 0.01);
    gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.5);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  }, []);

  const startGame = () => {
    setBoard(generateBoard(gridSize));
    setGameStarted(true);
    setGameOver(false);
    setHighlightedCells([]);
    setTimerKey(prev => prev + 1);
  };

  const endGame = () => {
    setGameOver(true);
    playGameOverSound();
  };

  const newGame = () => {
    setGameStarted(false);
    setGameOver(false);
    setBoard([]);
    setHighlightedCells([]);
  };

  const restartGame = () => {
    setBoard(generateBoard(gridSize));
    setGameOver(false);
    setHighlightedCells([]);
    setTimerKey(prev => prev + 1);
  };

  const handleFoundWord = (positions: [number, number][]) => {
    setHighlightedCells(positions);
  };

  const handleClear = () => {
    setHighlightedCells([]);
  };

  const toggleRules = () => {
    setShowRules(!showRules);
  };

  return (
    <div className="App">
      <h1>Boggle Game</h1>
      
      {!gameStarted && (
        <>
          <button 
            onClick={toggleRules} 
            className="rules-button"
          >
            {showRules ? 'Hide Rules' : 'Show Rules'}
          </button>
          
          {showRules && (
            <div className="rules-container">
              <h2>How to Play Boggle</h2>
              <div className="rules-content">
                <p>Boggle is a word game where you find words in a grid of letters.</p>
                
                <h3>Rules:</h3>
                <ol>
                  <li>Words must be at least 3 letters long.</li>
                  <li>Letters must be adjacent (horizontally, vertically, or diagonally).</li>
                  <li>You can't use the same letter cube twice in a word.</li>
                  <li>Words must be valid Spanish words.</li>
                  <li>Find as many words as you can before the timer runs out!</li>
                </ol>
                
                <h3>Scoring:</h3>
                <ul>
                  <li>3-4 letters: 1 point</li>
                  <li>5 letters: 2 points</li>
                  <li>6 letters: 3 points</li>
                  <li>7 letters: 5 points</li>
                  <li>8+ letters: 11 points</li>
                </ul>
                
                <p>After the timer ends, you can use the search function to verify if a word exists on the board.</p>
              </div>
            </div>
          )}
        </>
      )}
      
      {!gameStarted ? (
        <div className="controls">
          <div className="controls-row">
            <label>
              Grid Size:
              <input
                type="number"
                value={gridSize}
                onChange={(e) => setGridSize(Number(e.target.value))}
                min="3"
                max="10"
              />
            </label>
            <label>
              Timer (seconds):
              <input
                type="number"
                value={timerDuration}
                onChange={(e) => setTimerDuration(Number(e.target.value))}
                min="30"
                max="600"
              />
            </label>
          </div>
          <button onClick={startGame} className="start-button">Start Game</button>
        </div>
      ) : (
        <div className={gameOver ? 'game-over' : ''}>
          <Board board={board} highlightedCells={highlightedCells} />
          {!gameOver && (
            <Timer 
              key={timerKey} 
              duration={timerDuration} 
              onTimeUp={endGame} 
            />
          )}
          {gameOver && (
            <>
              <div className="game-over-message">TIME&apos;S UP!</div>
              <WordSearch board={board} onFoundWord={handleFoundWord} onClear={handleClear} />
            </>
          )}
          <div className="game-buttons">
            <button onClick={restartGame}>Restart Game</button>
            <button onClick={newGame}>New Game</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
